<script>
import { analyzePassword } from "@/utils/passwordStrength";
import {
  SNOOZE_DAY_OPTIONS,
  snoozePasswordPrompt,
  clearPasswordPromptSnooze,
} from "@/utils/passwordPromptSnooze";

export default {
  name: "InitialPasswordDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    username: {
      type: String,
      default: "",
    },
    openid: {
      type: String,
      default: "",
    },
    userId: {
      type: [String, Number],
      default: "",
    },
    otpStatus: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      submitting: false,
      snoozeDays: 7,
      snoozeDayOptions: SNOOZE_DAY_OPTIONS,
      form: {
        newPassword: "",
        confirmPassword: "",
        otpCode: "",
      },
    };
  },
  computed: {
    passwordMeta() {
      return analyzePassword(this.form.newPassword);
    },
    strengthPercent() {
      const map = { none: 0, weak: 33, medium: 66, strong: 100 };
      return map[this.passwordMeta.level] || 0;
    },
    strengthType() {
      const map = { none: "info", weak: "exception", medium: "warning", strong: "success" };
      return map[this.passwordMeta.level] || "info";
    },
    strengthLabel() {
      const level = this.passwordMeta.level;
      if (level === "weak") return this.$t("home.password.strength_weak");
      if (level === "medium") return this.$t("home.password.strength_medium");
      if (level === "strong") return this.$t("home.password.strength_strong");
      return "";
    },
    rules() {
      const rules = {
        newPassword: [
          {
            required: true,
            message: this.$t("home.password.newpassword_tip"),
            trigger: "blur",
          },
          { validator: this.validateNewPassword, trigger: "blur" },
        ],
        confirmPassword: [
          {
            required: true,
            message: this.$t("home.password.newpassword_check_tip"),
            trigger: "blur",
          },
          { validator: this.validateConfirmPassword, trigger: "blur" },
        ],
      };
      if (this.otpStatus) {
        rules.otpCode = [
          {
            required: true,
            message: this.$t("setting.system.otpbind_input"),
            trigger: "blur",
          },
          {
            pattern: /^[0-9]{6}$/,
            message: this.$t("setting.system.otpbind_input_error"),
            trigger: "blur",
          },
        ];
      }
      return rules;
    },
    dialogWidth() {
      return typeof window !== "undefined" && window.innerWidth < 768
        ? "92%"
        : "480px";
    },
    promptUser() {
      return {
        openid: this.openid,
        username: this.username,
        id: this.userId,
      };
    },
  },
  watch: {
    "form.newPassword"() {
      if (this.form.confirmPassword && this.$refs.form) {
        this.$refs.form.validateField("confirmPassword");
      }
    },
  },
  methods: {
    validateNewPassword(rule, value, callback) {
      if (!value) {
        callback();
        return;
      }
      const meta = analyzePassword(value);
      if (!meta.length) {
        callback(new Error(this.$t("home.password.newpassword_length")));
        return;
      }
      if (meta.typesCount < 3) {
        callback(new Error(this.$t("home.password.newpassword_type")));
        return;
      }
      callback();
    },
    validateConfirmPassword(rule, value, callback) {
      if (!value) {
        callback();
        return;
      }
      if (value !== this.form.newPassword) {
        callback(new Error(this.$t("home.password.newpassword_double")));
        return;
      }
      callback();
    },
    onOpened() {
      this.$nextTick(() => {
        if (this.$refs.newPasswordInput && this.$refs.newPasswordInput.focus) {
          this.$refs.newPasswordInput.focus();
        }
      });
    },
    resetForm() {
      this.form.newPassword = "";
      this.form.confirmPassword = "";
      this.form.otpCode = "";
      this.submitting = false;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate();
      });
    },
    onClosed() {
      this.resetForm();
    },
    close() {
      this.$emit("update:visible", false);
    },
    deferChange() {
      this.close();
    },
    snoozeAndClose() {
      const days = snoozePasswordPrompt(this.promptUser, this.snoozeDays);
      if (!days) {
        this.$message.error(this.$t("home.password.snooze_failed"));
        return;
      }
      this.$message.success(
        this.$t("home.password.snooze_done", { days: days })
      );
      this.$emit("snoozed");
      this.close();
    },
    submitForm() {
      if (this.submitting) return;
      this.$refs.form.validate((valid) => {
        if (!valid) {
          this.$message.error(this.$t("global.form.validate_failed"));
          return;
        }
        if (this.otpStatus) {
          this.verifyOtpThenChange();
        } else {
          this.sendPasswordChange();
        }
      });
    },
    authHeaders() {
      return { headers: { Openid: this.openid } };
    },
    verifyOtpThenChange() {
      this.submitting = true;
      this.$axios
        .post(
          this.apiuri,
          {
            action: "checkotptoken_online",
            code: this.form.otpCode,
          },
          this.authHeaders()
        )
        .then((response) => {
          if (response.data && response.data.status === 200) {
            this.sendPasswordChange();
          } else {
            this.submitting = false;
            this.$message.error(this.$t("setting.system.otpcheck_failed"));
          }
        })
        .catch(() => {
          this.submitting = false;
          this.$message.error(this.$t("setting.system.otpbind_error"));
        });
    },
    sendPasswordChange() {
      this.submitting = true;
      this.$axios
        .post(
          this.apiuri,
          {
            action: "changeadminpwd",
            password: this.form.confirmPassword,
          },
          this.authHeaders()
        )
        .then((response) => {
          this.submitting = false;
          if (response.data && response.data.status == 200) {
            clearPasswordPromptSnooze(this.promptUser);
            this.$message.success(this.$t("home.password.success"));
            this.close();
            this.$emit("success");
          } else {
            this.$message.error(
              (response.data && response.data.msg) ||
                this.$t("home.password.fail")
            );
          }
        })
        .catch(() => {
          this.submitting = false;
          this.$message.error(this.$t("home.password.fail"));
        });
    },
  },
};
</script>

<template>
  <el-dialog
    :title="$t('home.password.default')"
    :visible="visible"
    :width="dialogWidth"
    custom-class="initial-password-dialog"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :append-to-body="true"
    @opened="onOpened"
    @close="close"
    @closed="onClosed"
  >
    <el-alert
      type="warning"
      show-icon
      :closable="false"
      :title="$t('home.password.reason')"
      class="initial-password-alert"
    />

    <div v-if="username" class="initial-password-user">
      {{ $t("home.password.username") }}：{{ username }}
    </div>

    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      label-position="top"
      @submit.native.prevent="submitForm"
    >
      <el-form-item :label="$t('home.password.newpassword')" prop="newPassword">
        <el-input
          ref="newPasswordInput"
          v-model="form.newPassword"
          type="password"
          show-password
          autocomplete="new-password"
          :placeholder="$t('home.password.newpassword_tip')"
        />
        <div v-if="form.newPassword" class="initial-password-strength">
          <div class="initial-password-strength-row">
            <span>{{ $t("home.password.strength") }}</span>
            <span :class="'is-' + passwordMeta.level">{{ strengthLabel }}</span>
          </div>
          <el-progress
            :percentage="strengthPercent"
            :status="strengthType === 'info' ? undefined : strengthType"
            :show-text="false"
            :stroke-width="6"
          />
        </div>
      </el-form-item>

      <el-form-item
        :label="$t('home.password.newpassword_check')"
        prop="confirmPassword"
      >
        <el-input
          v-model="form.confirmPassword"
          type="password"
          show-password
          autocomplete="new-password"
          :placeholder="$t('home.password.newpassword_check_tip')"
          @keyup.enter.native="submitForm"
        />
      </el-form-item>

      <el-form-item
        v-if="otpStatus"
        :label="$t('setting.system.otp')"
        prop="otpCode"
      >
        <el-input
          v-model="form.otpCode"
          maxlength="6"
          autocomplete="one-time-code"
          :placeholder="$t('setting.system.otpbind_input')"
          @keyup.enter.native="submitForm"
        />
      </el-form-item>
    </el-form>

    <ul class="initial-password-reqs">
      <li :class="{ 'is-ok': passwordMeta.length }">
        {{ $t("home.password.req_length") }}
      </li>
      <li :class="{ 'is-ok': passwordMeta.lower }">
        {{ $t("home.password.req_lower") }}
      </li>
      <li :class="{ 'is-ok': passwordMeta.upper }">
        {{ $t("home.password.req_upper") }}
      </li>
      <li :class="{ 'is-ok': passwordMeta.digit }">
        {{ $t("home.password.req_digit") }}
      </li>
      <li :class="{ 'is-ok': passwordMeta.symbol }">
        {{ $t("home.password.req_symbol") }}
      </li>
    </ul>
    <p class="initial-password-hint">{{ $t("home.password.req_hint") }}</p>

    <div slot="footer" class="dialog-footer initial-password-footer">
      <div class="initial-password-snooze">
        <el-select v-model="snoozeDays" size="small" class="snooze-days">
          <el-option
            v-for="days in snoozeDayOptions"
            :key="days"
            :label="$t('home.password.snooze_days', { days: days })"
            :value="days"
          />
        </el-select>
        <el-button :disabled="submitting" @click="snoozeAndClose">
          {{ $t("home.password.snooze_action") }}
        </el-button>
      </div>
      <div>
        <el-button :disabled="submitting" @click="deferChange">
          {{ $t("home.password.later") }}
        </el-button>
        <el-button
          type="primary"
          :loading="submitting"
          @click="submitForm"
        >
          {{ $t("home.password.submit") }}
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<style lang="scss">
.initial-password-dialog {
  border-radius: 10px;

  .el-dialog__body {
    padding-top: 12px;
  }
}

.initial-password-alert {
  margin-bottom: 14px;
}

.initial-password-user {
  margin-bottom: 12px;
  color: #606266;
  font-size: 13px;
}

.initial-password-strength {
  margin-top: 8px;
}

.initial-password-strength-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 12px;
  color: #909399;

  .is-weak {
    color: #f56c6c;
  }
  .is-medium {
    color: #e6a23c;
  }
  .is-strong {
    color: #67c23a;
  }
}

.initial-password-reqs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  margin: 0 0 6px;
  padding: 0;
  list-style: none;
  font-size: 12px;
  color: #909399;

  li.is-ok {
    color: #67c23a;
  }
}

.initial-password-hint {
  margin: 0;
  font-size: 12px;
  color: #c0c4cc;
}

.initial-password-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.initial-password-snooze {
  display: flex;
  align-items: center;
  gap: 8px;
}

.initial-password-snooze .snooze-days {
  width: 96px;
}
</style>
